<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "$lib/components/ui/command"
  import { Input } from "$lib/components/ui/input"
  import { Label } from "$lib/components/ui/label"
  import { Popover, PopoverButton, PopoverContent, PopoverTrigger } from "$lib/components/ui/popover"
  import type { _response } from "$lib/data/type/_response"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { sleep } from "$lib/sleep"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import FilterIcon from "@lucide/svelte/icons/filter"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import { createInfiniteQuery, createQuery } from "@tanstack/svelte-query"
  import DialogDetail from "./_components/dialog-detail.svelte"
  import { type Customer, CustomerEmpty, type Relation } from "./_types"

  const prefix = $derived(page.params.prefix ?? "0")

  let sentinel = $state<HTMLDivElement>()

  let category = $state<Relation>({ id: 0, name: "", order: 0 })

  let selected = $state<Customer>(CustomerEmpty)
  const detailDialog = useDialog()

  const categories = createQuery(() => ({
    queryKey: ["customer-category", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customers/master-category`)
      return response.data.result ?? []
    }
  }))

  const search = createSearch()
  const query = createInfiniteQuery(() => ({
    queryKey: ["customer", {
      prefix,
      category_id: category.id,
      search: search.debounced
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Customer[]>>(`/api/${prefix}/customers`, {
        params: {
          page: pageParam,
          category_id: category.id,
          search: search.debounced
        }
      })

      return response.data.result ?? []
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))
  const rows = $derived(query.data?.pages.flat() ?? [])

  async function handleRefresh() {
    await sleep(1)
    void query.refetch()
  }

  function handleCategory(value: Relation) {
    category = value
    document.keyEsc()
  }

  $effect(() => {
    if (!sentinel || !query.hasNextPage || query.isFetching) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void query.fetchNextPage()
      },
      { threshold: 0.5 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Kembali">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Customer</h1>
      </div>
      <div></div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group"
        data-loading={query.isFetching}
        disabled={query.isFetching}
        onclick={handleRefresh}>
        <LoaderCircleIcon
          class="invisible absolute animate-spin group-data-[loading=true]:visible"
          aria-hidden="true"/>
        <RefreshCwIcon
          class="group-data-[loading=true]:invisible"
          aria-hidden="true"/>
      </Button>
      <Input
        class="flex-1"
        placeholder="Cari..."
        value={search.state}
        oninput={(event) => search.set(event.currentTarget.value)}/>
      <Popover>
        <PopoverTrigger>
          {#snippet child({ props })}
            <Button
              {...props}
              variant="outline"
              size="icon">
              <FilterIcon aria-hidden="true"/>
            </Button>
          {/snippet}
        </PopoverTrigger>
        <PopoverContent
          align="end"
          class="relative grid w-72 gap-4 p-4"
          onOpenAutoFocus={(event) => event.preventDefault()}>
          <div class="space-y-1">
            <Label>Kategori</Label>
            <Popover>
              <PopoverButton
                class="w-full min-w-0"
                value={category.name || "Semua Kategori"}/>
              <PopoverContent>
                <Command>
                  <CommandList class="min-h-20">
                    <CommandEmpty>Tidak ada data</CommandEmpty>
                    <CommandGroup>
                      {#each categories.data ?? [] as item (item.id)}
                        <CommandItem
                          value={item.name}
                          onclick={() => handleCategory(item)}>
                          {item.name}
                        </CommandItem>
                      {/each}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </nav>
  <div class="min-h-full">
    {#if query.isError}
      <div class="flex flex-col items-center gap-2 px-3 py-4 text-center text-xs">
        Gagal memuat data
        <Button
          variant="outline"
          onclick={() => query.refetch()}>
          Muat Ulang
        </Button>
      </div>
    {:else if !query.isFetching && rows.isEmpty()}
      <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
    {:else}
      {#each rows as customer (customer.id)}
        <Button
          variant="ghost"
          class="h-auto w-full justify-start rounded-none px-3 py-2 text-start"
          onclick={() => {
            selected = customer
            detailDialog.setOpen(true)
          }}>
          <span>
            <span class="block text-xs font-light tabular-nums tracking-tight">{customer.number}</span>
            <span class="block">{customer.name}</span>
          </span>
        </Button>
      {/each}
    {/if}
  </div>
  <div
    bind:this={sentinel}
    class="h-1">
  </div>
</main>
<DialogDetail
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
  select={selected}
  prefix={prefix}/>
